import { GridColDef } from '@mui/x-data-grid';
import { IconButton, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../../../theme';

interface FieldMapping {
  [key: string]: string; // dynamic keys
}

interface LabelMapping {
  [key: string]: string;
}

interface GetReviewColumnsProps {
  onEdit: (row: any) => void;
  onDelete: (ids: string[]) => void;
  onDisapprove: (ids: string[]) => void;
  onApprove: (ids: string[]) => void;
  fields: FieldMapping; // required since logic depends on it
  labels?: LabelMapping;
  translationKey?: string;
}

export const getReviewColumns = ({
  onEdit,
  onDelete,
  onDisapprove,
  onApprove,
  fields,
  labels = {},
  translationKey = 'review.itemReview',
}: GetReviewColumnsProps): GridColDef[] => {
  const { t } = useTranslation();
  const idField = fields.id || 'id';
  const reviewStatusField = fields.reviewStatus || 'reviewApprovalStatus';

  return Object.entries(fields).map(([key, value]) => {
    const label = labels[key] || t(`${translationKey}.columns.${key}`) || key;

    // Handle special fields
    if (key === 'view') {
      return {
        field: value,
        headerName: label,
        flex: 0.5,
        sortable: false,
        renderCell: (params) => (
          <IconButton size="small" onClick={() => onEdit(params.row)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        ),
      };
    }

    if (key === 'status') {
      return {
        field: value,
        headerName: label,
        flex: 0.8,
        sortable: false,
        renderCell: (params) => {
          const isApproved = params.row?.[reviewStatusField];
          const rowId = params.row?.[idField];

          return !isApproved ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => onApprove([rowId])}
              startIcon={<ThumbDownIcon />}
              sx={{
                // Disapproved status - keep orange for clarity
                backgroundColor: COLORS.primary.main,
                color:   COLORS.secondary.main,
                '&:hover': { backgroundColor: COLORS.primary.dark },
                textTransform: 'none',
              }}
            >
              {labels.disapprove || t(`${translationKey}.actions.disapprove`)}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => onDisapprove([rowId])}
              startIcon={<ThumbUpIcon />}
              sx={{
                // Approved status - use green to match Admin Panel
                backgroundColor: '#4caf50',
                color: '#fff',
                '&:hover': { backgroundColor: '#43a047' },
                textTransform: 'none',
              }}
            >
              {labels.approve || t(`${translationKey}.actions.approve`)}
            </Button>
          );
        },
      };
    }

    if (key === 'delete') {
      return {
        field: value,
        headerName: label,
        flex: 0.5,
        sortable: false,
        renderCell: (params) => {
          const rowId = params.row?.[idField];
          return (
            <IconButton color="error" onClick={() => onDelete([rowId])}>
              <DeleteIcon />
            </IconButton>
          );
        },
      };
    }

    // Default column
    return {
      field: value,
      headerName: label,
      flex: 1,
    };
  });
};
